<template lang="pug">
web-app-layout(
  :showLogo="true"
  :noPadding="true"
  :hasGradient="showGradient"
  :whiteMode="showGradient"
)
  // Full-width gradient background (outside container)
  .gradient-background-full(v-if="showGradient && staticPageData")
  
  .static-page-container(:class="{ 'has-gradient': showGradient && isHomePage }")
    // Loading state
    web-page-skeleton(v-if="loading" :sections="3" :cardsPerSection="6")

    // Error state
    .error-container(v-else-if="error")
      el-result(
        icon="error"
        title="Error"
        :sub-title="errorMessage"
      )
        template(#extra)
          el-button(type="primary" @click="handleRetry") Retry

    // Loaded state
    .content-container(v-else-if="staticPageData")
      .blocks-wrapper
        component(
          v-for="block in sortedBlocks"
          :key="block.id"
          :is="getBlockComponent(block.blockType)"
          :block="block"
        )
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted, watch,
} from 'vue';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import WebPageSkeleton from '../components/WebPageSkeleton.vue';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { useStaticPage } from './useStaticPage';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { StaticPageData, BlockTypes } from './types';
import BannerBlock from './blocks/BannerBlock.vue';
import SearchBarBlock from './blocks/SearchBarBlock.vue';
import StickersBlock from './blocks/StickersBlock.vue';
import OrganizationsBlock from './blocks/OrganizationsBlock.vue';
import RichTextBlock from './blocks/RichTextBlock.vue';
import AnnouncementBlock from './blocks/AnnouncementBlock.vue';
import QuestionBlock from './blocks/QuestionBlock.vue';
import CollectionsBlock from './blocks/CollectionsBlock.vue';
import ListBlock from './blocks/ListBlock.vue';

export default defineComponent({
  name: 'StaticPage',
  components: {
    WebAppLayout,
    WebPageSkeleton,
    BannerBlock,
    SearchBarBlock,
    StickersBlock,
    OrganizationsBlock,
    RichTextBlock,
    AnnouncementBlock,
    QuestionBlock,
    CollectionsBlock,
    ListBlock,
  },
  props: {
    slug: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const {
      getStaticPage, loading, error, errorMessage,
    } = useStaticPage();

    // State
    const staticPageData = ref<StaticPageData | null>(null);

    // Computed
    const isHomePage = computed(() => props.slug === 'home-page');

    const showGradient = computed(
      () => staticPageData.value?.greenGradientEnabled || false,
    );

    const sortedBlocks = computed(() => {
      if (!staticPageData.value?.blocks) return [];

      // Blocks are already sorted by the composable
      return staticPageData.value.blocks;
    });

    // Methods
    const fetchData = async () => {
      const data = await getStaticPage(props.slug);
      staticPageData.value = data;
    };

    const handleRetry = () => {
      fetchData();
    };

    const getBlockComponent = (blockType: string) => {
      const componentMap: { [key: string]: string } = {
        [BlockTypes.BANNER]: 'BannerBlock',
        [BlockTypes.SEARCH_BAR]: 'SearchBarBlock',
        [BlockTypes.STICKERS]: 'StickersBlock',
        [BlockTypes.ORGANIZATIONS]: 'OrganizationsBlock',
        [BlockTypes.RICH_TEXT]: 'RichTextBlock',
        [BlockTypes.ANNOUNCEMENT]: 'AnnouncementBlock',
        [BlockTypes.QUESTION]: 'QuestionBlock',
        [BlockTypes.COLLECTIONS]: 'CollectionsBlock',
        [BlockTypes.LIST]: 'ListBlock',
      };

      return componentMap[blockType] || 'div';
    };

    // Lifecycle
    onMounted(() => {
      fetchData();
    });

    // Watch for slug changes to reload content when navigating between static pages
    watch(() => props.slug, (newSlug, oldSlug) => {
      if (newSlug !== oldSlug) {
        fetchData();
      }
    });

    return {
      loading,
      error,
      errorMessage,
      staticPageData,
      isHomePage,
      showGradient,
      sortedBlocks,
      handleRetry,
      getBlockComponent,
    };
  },
});
</script>

<style lang="scss">
// Full-width gradient that breaks out of container
// NOTE: Not scoped so it can properly break out to full viewport width
.gradient-background-full {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(50% - 50vw);
  height: 200px;
  background: linear-gradient(
    180deg,
    #225c6b 0%,
    #225c6b 5%,
    rgba(34, 92, 107, 0.6) 60%,
    rgba(34, 92, 107, 0) 100%
  );
  z-index: 0;
  pointer-events: none;

  @media (min-width: 768px) {
    height: 250px;
  }

  // Align with full-bleed header when desktop sidebar is visible
  @media (min-width: 1024px) {
    .pl-main-menu-width & {
      margin-left: calc(50% - 50vw - 150px);
    }
  }
}
</style>

<style lang="scss" scoped>
// Scoped styles for the rest of the component
.static-page-container {
  position: relative;
  min-height: 100vh;
  margin-top: 0;
  z-index: 1;

  &.has-gradient {
    margin-top: 0;
  }
}

// Error State
.error-container {
  padding: 80px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;

  @media (min-width: 768px) {
    padding: 100px 40px;
  }

  @media (min-width: 1024px) {
    padding: 120px 64px;
  }

  :deep(.el-result__title) {
    color: #1a1a1a;
    font-weight: 700;
  }

  :deep(.el-result__subtitle) {
    color: #6b7280;
  }

  :deep(.el-button--primary) {
    background: linear-gradient(90deg, #14b8a6 0%, #0d9488 100%);
    border: none;
    border-radius: 9999px;
    padding: 12px 24px;
    font-weight: 600;

    &:hover {
      transform: scale(1.02);
    }
  }
}

// Content Container
.content-container {
  position: relative;
  width: 100%;
  background: #ffffff;
  // No max-width - constrained by parent WebAppLayout
}

// When gradient is present, pull content up to overlap
.static-page-container.has-gradient .content-container {
  background: transparent; // Transparent so gradient shows through
  margin-top: -300px;
  
  @media (min-width: 768px) {
    margin-top: -300px;
  }
}

.blocks-wrapper {
  position: relative;
  z-index: 1;
  padding: 32px 0 64px;
  width: 100%;
  // No max-width - constrained by parent WebAppLayout

  @media (min-width: 768px) {
    padding: 40px 0 80px;
  }

  @media (min-width: 1024px) {
    padding: 48px 0 96px;
  }
}
</style>
